import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import Spinner from 'react-bootstrap/Spinner'

type DropdownOption = {
  label: string
  value: string
  disabled?: boolean
}

type MenuDirection = 'up' | 'down'

interface ActionButtonProps {
  label: string
  /**
   * Optional leading content lets shared action buttons display a contextual
   * icon without duplicating the button primitive in a feature component.
   */
  startIcon?: ReactNode
  type?: 'button' | 'select'
  variant?: string
  width?: number
  disabled?: boolean
  onClick?: () => void
  options?: DropdownOption[]
  onSelect?: (value: string) => void
  backgroundColor?: string
  loading?: boolean
}

const ActionButtonStyled = styled.button<{
  $variant?: string
  $width?: number
  disabled?: boolean
}>`
  width: ${(props) => (props.$width ? `${props.$width}px` : '79px')};
  height: 33px;
  outline: none;
  border: ${(props) =>
    props.$variant === 'clear'
      ? `1px solid ${props.theme.colors.border.primary}`
      : 'none'};
  border-radius: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: ${(props) => {
    switch (props.$variant) {
      case 'warn':
        return props.theme.colors.surface.warn
      case 'info':
        return props.theme.colors.brand.primaryBlue
      case 'info-dark':
        return props.theme.colors.surface.infoDark
      case 'success':
        return props.theme.colors.surface.success
      case 'error':
        return props.theme.colors.surface.errorDark
      case 'clear':
        return props.theme.colors.card.background
      default:
        return props.theme.colors.surface.muted
    }
  }};
  color: ${(props) => {
    if (props.theme.mode === 'dark' && props.$variant === 'clear') {
      return '#ffffff'
    }

    if (props.theme.mode === 'light' && props.$variant === 'clear') {
      return '#000000'
    }

    if (props.$variant === 'warn') {
      return '#000000'
    }

    return '#FFFFFF'
  }};
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.border.active};
    outline-offset: 2px;
  }
  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;
  `};
`

const DropdownContainer = styled.div`
  position: relative;
  display: inline-flex;
`

const TriggerButton = styled.button<{
  $width?: number
  $backgroundColor: string
  disabled?: boolean
}>`
  width: ${(props) => (props.$width ? `${props.$width}px` : '79px')};
  height: 33px;
  border: none;
  border-radius: 40px;
  outline: none;
  background: ${(props) => props.$backgroundColor};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-weight: 400;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.border.active};
    outline-offset: 2px;
  }

  ${(props) =>
    props.disabled
      ? `
    cursor: not-allowed;
    opacity: 0.5;
  `
      : `
    cursor: pointer;
  `};
`

const DropdownMenu = styled.ul<{ $direction: MenuDirection }>`
  position: absolute;
  top: ${(props) =>
    props.$direction === 'down' ? 'calc(100% + 6px)' : 'auto'};
  bottom: ${(props) =>
    props.$direction === 'up' ? 'calc(100% + 6px)' : 'auto'};
  left: 0;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  background: ${(props) => props.theme.colors.card.background};
  z-index: 20;
`

const DropdownItem = styled.button<{ disabled?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  text-align: left;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 13px;
  padding: 8px 10px;
  white-space: nowrap;
  &:hover {
    background: ${(props) => props.theme.colors.surface.muted};
  }
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.border.active};
    outline-offset: 2px;
  }
`

const ActionButton = ({
  label,
  startIcon,
  type = 'button',
  variant,
  width,
  onClick,
  disabled,
  options,
  onSelect,
  backgroundColor,
  loading = false
}: ActionButtonProps) => {
  const isSelect = type === 'select'
  const [open, setOpen] = useState(false)
  const [menuDirection, setMenuDirection] = useState<MenuDirection>('down')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLUListElement | null>(null)

  useEffect(() => {
    if (!isSelect) {
      return
    }

    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onEscape)

    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onEscape)
    }
  }, [isSelect])

  useEffect(() => {
    if (!isSelect || !open || !containerRef.current || !menuRef.current) {
      return
    }

    const containerRect = containerRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current.offsetHeight
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - containerRect.bottom
    const spaceAbove = containerRect.top

    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      setMenuDirection('up')
      return
    }

    setMenuDirection('down')
  }, [isSelect, open, options?.length])

  const onOptionClick = (value: string, isDisabled?: boolean) => {
    if (isDisabled) {
      return
    }

    onSelect?.(value)
    setOpen(false)
  }

  if (isSelect) {
    return (
      <DropdownContainer ref={containerRef}>
        <TriggerButton
          type='button'
          $width={width}
          $backgroundColor={backgroundColor || '#49B0B9'}
          disabled={disabled || loading}
          onClick={() => {
            if (!open) {
              setMenuDirection('down')
            }
            setOpen((previous) => !previous)
          }}
        >
          {loading ? (
            <Spinner
              as='span'
              animation='border'
              size='sm'
              role='status'
              aria-hidden='true'
            />
          ) : (
            <>
              {label}
              <ChevronIcon
                size='sm'
                direction={open ? 'up' : 'down'}
                color='#ffffff'
                disableHover
                containerStyle={{ padding: 0 }}
              />
            </>
          )}
        </TriggerButton>

        {open && (options || []).length > 0 && (
          <DropdownMenu ref={menuRef} $direction={menuDirection}>
            {(options || []).map((option) => (
              <li key={option.value}>
                <DropdownItem
                  type='button'
                  disabled={option.disabled}
                  onClick={() => onOptionClick(option.value, option.disabled)}
                >
                  {option.label}
                </DropdownItem>
              </li>
            ))}
          </DropdownMenu>
        )}
      </DropdownContainer>
    )
  }

  return (
    <ActionButtonStyled
      $variant={variant}
      $width={width}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <Spinner
          as='span'
          animation='border'
          size='sm'
          role='status'
          aria-hidden='true'
        />
      ) : (
        <>
          {startIcon}
          {label}
        </>
      )}
    </ActionButtonStyled>
  )
}

export type { DropdownOption, ActionButtonProps }
export default ActionButton
